import React, { useCallback } from 'react';
import { noop } from 'lodash';

import IP from 'constants/form/inputParser.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import call from 'utils/functions/call.util';
import asObject from 'utils/objects/asObject.util';
import parseText from 'utils/parsers/parseText.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import asInput from 'utils/strings/asInput.util';

import CN from './PhoneInput.module.scss';

const CTAG = 'PhoneInput';

const PhoneInput = ({
  name,
  onBlur = noop,
  onChange = noop,
  onClick = noop,
  onFocus = noop,
  tag,
  value: valueFromProps,
  children, // removed from extra so it is not passed to the <input>
  ...extra
}) => {
  const t = fromAnyToTrimmed(tag);

  const [localValue, setLocalValue] = useStateWhileMounted(valueFromProps);
  const { val: isDirty, inv: isClean, set: setDirty } = useTip(false);
  const { val: isError, inv: isValid, set: setError } = useTip(false);
  const { val: isFocused, yay: gotFocus, nay: lostFocus } = useTip(false);

  const prepare = useCallback(
    (ev) => {
      const value = fromEventToValue(ev);
      const parsed = parseText({ value });
      const dirty = value !== localValue;

      return { ...parsed, tag: t, parser: IP.text, name, dirty, ev, event: ev };
    },
    [localValue, name, t],
  );

  const reconcile = useCallback(
    ({ result, prepared }) => {
      const { error, value, dirty } = asObject(result);

      const newValue = isMissing(value) ? prepared.raw : value;
      const newDirty = isMissing(dirty) ? newValue !== valueFromProps : dirty;

      setLocalValue(newValue);
      setDirty(newDirty);
      setError(error);
    },
    [setDirty, setError, setLocalValue, valueFromProps],
  );

  const handleAs = useCallback(
    (name, fn, ev) => {
      const prepared = prepare(ev);
      const result = call(`${CTAG}.${name}`, fn, prepared);
      return reconcile({ result, prepared });
    },
    [prepare, reconcile],
  );

  const handleChange = useCallback((ev) => handleAs('onChange', onChange, ev), [handleAs, onChange]);
  const handleClick = useCallback((ev) => handleAs('onClick', onClick, ev), [handleAs, onClick]);

  const handleFocus = useCallback(
    (ev) => {
      gotFocus();
      return handleAs('onFocus', onFocus, ev);
    },
    [gotFocus, handleAs, onFocus],
  );

  const handleBlur = useCallback(
    (ev) => {
      lostFocus();
      return handleAs(`onBlur`, onBlur, ev);
    },
    [handleAs, lostFocus, onBlur],
  );

  return (
    <input
      type="tel"
      inputMode="tel"
      autoComplete="off"
      {...extra}
      className={CN.component}
      data-bem="PhoneInput"
      data-tag={t}
      name={name}
      value={asInput(localValue)}
      data-field-clean={isClean}
      data-field-dirty={isDirty}
      data-field-error={isError}
      data-field-focus={isFocused}
      data-field-valid={isValid}
      onBlur={handleBlur}
      onChange={handleChange}
      onClick={handleClick}
      onFocus={handleFocus}
    />
  );
};

// noinspection JSUnusedGlobalSymbols
export default PhoneInput;
